import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

const BG_IMAGES = {
  morning: [
    'https://images.unsplash.com/photo-1660098932634-a10f68f029e4?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1573501596697-a5b0ec1b4897?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1660098932605-13b74098242b?w=1920&q=80&auto=format',
  ],
  midday: [
    'https://images.unsplash.com/photo-1707857727326-4ef476220afc?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1623876355063-15d1d5e5b89b?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1595621060436-90e9f966e9d9?w=1920&q=80&auto=format',
  ],
  evening: [
    'https://images.unsplash.com/photo-1770944661062-cd16108831e2?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1762402511905-c3ad4af1db94?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1760311462862-54b1c8fe68c0?w=1920&q=80&auto=format',
  ],
  night: [
    'https://images.unsplash.com/photo-1770555564445-ce9e258e00bb?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1546757545-d680d414fbbb?w=1920&q=80&auto=format',
    'https://images.unsplash.com/photo-1675750317972-23e9d858c4a2?w=1920&q=80&auto=format',
  ],
};

function getTimeOfDay(hour) {
  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'midday';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
}

function pickSeeded(arr, seed) {
  return arr[seed % arr.length];
}

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  // themeMode: 'light' | 'dynamic' | 'dark'
  const [themeMode, setThemeModeState] = useState(() => localStorage.getItem('swell_theme_mode') || 'dark');
  const [locationOverride, setLocationOverride] = useState(() => {
    const stored = localStorage.getItem('swell_location_override');
    return stored ? JSON.parse(stored) : null;
  });
  const [remoteHour, setRemoteHour] = useState(null);
  const [localTime, setLocalTime] = useState('');
  const [timeOfDay, setTimeOfDay] = useState(() => getTimeOfDay(new Date().getHours()));

  // Seed from the day so we get consistent image per period per day
  const daySeed = useRef(Math.floor(Date.now() / 86400000));
  const [bgImage, setBgImage] = useState(() => pickSeeded(BG_IMAGES[getTimeOfDay(new Date().getHours())], daySeed.current));

  // Compute effective hour: remoteHour (if location override active) or local hour
  const getEffectiveHour = useCallback(() => {
    if (locationOverride?.timezone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: locationOverride.timezone,
          hour: 'numeric',
          hour12: false,
        });
        return parseInt(formatter.format(new Date()), 10);
      } catch {
        return new Date().getHours();
      }
    }
    return new Date().getHours();
  }, [locationOverride]);

  const getEffectiveTimeStr = useCallback(() => {
    if (locationOverride?.timezone) {
      try {
        return new Intl.DateTimeFormat('en-US', {
          timeZone: locationOverride.timezone,
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        }).format(new Date());
      } catch { /* fall through */ }
    }
    return new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }, [locationOverride]);

  // isDark depends on themeMode
  const isDark = themeMode === 'dark' ? true
    : themeMode === 'light' ? false
    : (timeOfDay === 'evening' || timeOfDay === 'night');

  // Update time every 30s
  useEffect(() => {
    const update = () => {
      const hour = getEffectiveHour();
      const newPeriod = getTimeOfDay(hour);
      setTimeOfDay(prev => {
        if (prev !== newPeriod) {
          setBgImage(pickSeeded(BG_IMAGES[newPeriod], daySeed.current));
        }
        return newPeriod;
      });
      setLocalTime(getEffectiveTimeStr());
    };
    update();
    const interval = setInterval(update, 30000);
    return () => clearInterval(interval);
  }, [getEffectiveHour, getEffectiveTimeStr]);

  // Apply theme class to body
  useEffect(() => {
    document.body.classList.toggle('light-theme', !isDark);
    document.body.classList.toggle('dark-theme', isDark);
  }, [isDark]);

  const setThemeMode = useCallback((mode) => {
    setThemeModeState(mode);
    localStorage.setItem('swell_theme_mode', mode);
  }, []);

  const updateLocationOverride = useCallback(async (loc) => {
    if (!loc) {
      localStorage.removeItem('swell_location_override');
      setLocationOverride(null);
      setRemoteHour(null);
      return;
    }
    // Fetch timezone from backend
    try {
      const res = await fetch(`${API}/api/timezone?city=${encodeURIComponent(loc.city)}`);
      if (res.ok) {
        const data = await res.json();
        const enriched = { city: data.city || loc.city, timezone: data.timezone };
        localStorage.setItem('swell_location_override', JSON.stringify(enriched));
        setLocationOverride(enriched);
        setRemoteHour(data.hour);
        // Immediately update theme for new timezone
        const newPeriod = getTimeOfDay(data.hour);
        setTimeOfDay(newPeriod);
        setBgImage(pickSeeded(BG_IMAGES[newPeriod], daySeed.current));
        setLocalTime(data.local_time || '');
      } else {
        // Fallback: set without timezone
        const fallback = { city: loc.city };
        localStorage.setItem('swell_location_override', JSON.stringify(fallback));
        setLocationOverride(fallback);
      }
    } catch {
      const fallback = { city: loc.city };
      localStorage.setItem('swell_location_override', JSON.stringify(fallback));
      setLocationOverride(fallback);
    }
  }, []);

  return (
    <ThemeContext.Provider value={{
      timeOfDay,
      isDark,
      themeMode,
      setThemeMode,
      bgImage,
      localTime,
      locationOverride,
      updateLocationOverride,
      getEffectiveHour,
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
